import { PointerEvent, useEffect, useMemo, useRef, useState } from "react";
import mirrorRing from "../assets/images/icons/mirror-rings.svg";
import MirrorActivityLayout from "../components/MirrorActivityLayout";
import { useTranslation } from "../components/Localization";
import { saveGameScore } from "../services/gameScores";
import { usePageGame } from "../hooks/usePageGame";

function getAvailableLetters(words: string[]) {
  return Array.from(new Set(words.join("").replace(/\s/g, "").toLocaleUpperCase().split("")))
    .sort((first, second) => first.localeCompare(second));
}

function initialLetters(solution: string[], availableLetters: string[], editablePositions: Set<string>) {
  const values: Record<string, number> = {};
  editablePositions.forEach((key) => {
    const [rowIndex, letterIndex] = key.split("-").map(Number);
    const targetIndex = availableLetters.indexOf(solution[rowIndex][letterIndex]);
    const offset = ((rowIndex + 1) * (letterIndex + 2)) % Math.max(1, availableLetters.length - 1) + 1;
    values[key] = (targetIndex + offset) % availableLetters.length;
  });
  return values;
}

function distanceToTarget(currentIndex: number, targetIndex: number, letterCount: number) {
  const directDistance = Math.abs(currentIndex - targetIndex);
  return Math.min(directDistance, letterCount - directDistance);
}

export function ringsScoreFromUnnecessaryRotations(unnecessaryRotations: number) {
  return Math.max(5, 15 - unnecessaryRotations);
}

export default function MirrorRingsGamePage() {
  const { t } = useTranslation();
  const { game } = usePageGame("tukorterem", "guess_text");
  const solution = useMemo(() => (game?.items.find((item) => item.kind === "text")?.textContent || "").trim().toLocaleUpperCase().split(/\s+/).filter(Boolean), [game]);
  const availableLetters = useMemo(() => getAvailableLetters(solution), [solution]);
  const editablePositions = useMemo(() => {
    const positions = solution.flatMap((word, rowIndex) => word.split("").map((_, letterIndex) => `${rowIndex}-${letterIndex}`).slice(1));
    return new Set(positions.filter((_, index) => index % 2 === 0).slice(0, 8));
  }, [solution]);
  const [letters, setLetters] = useState<Record<string, number>>({});
  const lettersRef = useRef(letters);
  const [unnecessaryRotations, setUnnecessaryRotations] = useState(0);
  const [score, setScore] = useState<number | null>(null);
  const unnecessaryRotationsRef = useRef(0);
  const completedRef = useRef(false);
  const dragStarts = useRef<Record<string, { lastY: number; moved: boolean }>>({});

  useEffect(() => {
    const initial = initialLetters(solution, availableLetters, editablePositions);
    lettersRef.current = initial;
    setLetters(initial);
    setUnnecessaryRotations(0);
    setScore(null);
    unnecessaryRotationsRef.current = 0;
    completedRef.current = false;
  }, [availableLetters, editablePositions, solution]);

  const isCorrect = (key: string) => {
    const [rowIndex, letterIndex] = key.split("-").map(Number);
    return availableLetters[letters[key]] === solution[rowIndex][letterIndex];
  };
  const complete = editablePositions.size > 0 && Array.from(editablePositions).every(isCorrect);

  const spin = (key: string, direction: 1 | -1) => {
    if (completedRef.current) return;
    const current = lettersRef.current;
    const [rowIndex, letterIndex] = key.split("-").map(Number);
    const targetIndex = availableLetters.indexOf(solution[rowIndex][letterIndex]);
    const nextIndex = (current[key] + direction + availableLetters.length) % availableLetters.length;
    const currentDistance = distanceToTarget(current[key], targetIndex, availableLetters.length);
    const nextDistance = distanceToTarget(nextIndex, targetIndex, availableLetters.length);

    if (nextDistance >= currentDistance) {
      unnecessaryRotationsRef.current += 1;
      setUnnecessaryRotations(unnecessaryRotationsRef.current);
    }

    const next = { ...current, [key]: nextIndex };
    lettersRef.current = next;
    setLetters(next);

    const isComplete = Array.from(editablePositions).every((position) => {
      const [wordIndex, characterIndex] = position.split("-").map(Number);
      return availableLetters[next[position]] === solution[wordIndex][characterIndex];
    });
    if (isComplete) {
      completedRef.current = true;
      const finalScore = ringsScoreFromUnnecessaryRotations(unnecessaryRotationsRef.current);
      saveGameScore("rings", finalScore);
      setScore(finalScore);
    }
  };

  const startSpin = (event: PointerEvent<HTMLButtonElement>, key: string) => {
    dragStarts.current[key] = { lastY: event.clientY, moved: false };
    event.currentTarget.setPointerCapture(event.pointerId);
  };

  const moveSpin = (event: PointerEvent<HTMLButtonElement>, key: string) => {
    const drag = dragStarts.current[key];
    if (!drag) return;
    const distance = event.clientY - drag.lastY;
    if (Math.abs(distance) < 18) return;
    spin(key, distance > 0 ? -1 : 1);
    dragStarts.current[key] = { lastY: event.clientY, moved: true };
  };

  const finishSpin = (event: PointerEvent<HTMLButtonElement>, key: string) => {
    const drag = dragStarts.current[key];
    if (!drag) return;
    if (!drag.moved) spin(key, 1);
    delete dragStarts.current[key];
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
  };

  return (
    <MirrorActivityLayout
      instruction={t("accessibility.ringsInstruction")}
      pageType="game"
      step={6}
      progressIcon="bottom"
      backTo="/tukorterem/festmeny-2"
      nextTo="/tukorterem/eredmeny"
      showProgressIcon={false}
      nextDisabled={!complete}
      contentClassName="mirror-rings-content"
    >
      <div className="mirror-rings-pair" role="img" aria-label={t("accessibility.rings")}>
        <img src={mirrorRing} alt="" />
        <img src={mirrorRing} alt="" />
      </div>
      <div className="mirror-letter-spinner" aria-label={t("accessibility.ringSpinner")}>
        {solution.map((word, rowIndex) => (
          <div className="mirror-letter-row" key={word}>
            {word.split("").map((letter, letterIndex) => {
              const key = `${rowIndex}-${letterIndex}`;
              if (!editablePositions.has(key)) {
                return <span className="mirror-letter-fixed" key={key}><span>{letter}</span></span>;
              }
              const correct = isCorrect(key);
              return <button className={`mirror-letter-wheel ${correct ? "is-correct" : ""}`} type="button" key={key} onPointerDown={(event) => startSpin(event, key)} onPointerMove={(event) => moveSpin(event, key)} onPointerUp={(event) => finishSpin(event, key)} onPointerCancel={() => delete dragStarts.current[key]} aria-label={t("accessibility.ringLetter", { row: rowIndex + 1, letter: letterIndex + 1 })}>
                <output aria-live="polite">{availableLetters[letters[key]]}</output>
              </button>;
            })}
          </div>
        ))}
      </div>
      <div className="mirror-rings-score" aria-live="polite">
        <span>{t("score.unnecessaryRotations", { count: unnecessaryRotations })}</span>
        {score !== null && <strong>{t("score.gameResult", { score, max: 15 })}</strong>}
      </div>
    </MirrorActivityLayout>
  );
}
